"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSession } from "next-auth/react";
import { useCallback, useMemo } from "react";
import { toast } from "sonner";
import type {
  UpdateCustomerInput,
  UpdateCustomerResponse,
} from "@/app/account/types";
import type { ExtendedSession } from "@/lib/auth/types";
import { orpc } from "@/utils/orpc";

/**
 * Unified user hook that consolidates all user-related functionality
 * Replaces: useAuth, useUpdateProfile, useCustomerProfileUpdate, usePermissions, useAuthStatus
 */
export function useUser() {
  const { data: session, status, update } = useSession();
  const queryClient = useQueryClient();

  const typedSession = useMemo(
    () => session as ExtendedSession | null,
    [session]
  );

  // User data and computed properties
  const user = typedSession?.user || null;
  const tags = useMemo(() => user?.tags || [], [user?.tags]);

  const fullName = useMemo(() => {
    if (!(user?.firstName || user?.lastName)) return null;
    return [user.firstName, user.lastName].filter(Boolean).join(" ");
  }, [user?.firstName, user?.lastName]);

  // Authentication state
  const isAuthenticated = status === "authenticated";
  const isLoading = status === "loading";
  const isUnauthenticated = status === "unauthenticated";

  // Tag utility functions
  const hasTag = useCallback((tag: string) => tags.includes(tag), [tags]);
  const hasAnyTag = useCallback(
    (searchTags: string[]) => searchTags.some((t) => tags.includes(t)),
    [tags]
  );
  const hasAllTags = useCallback(
    (searchTags: string[]) => searchTags.every((t) => tags.includes(t)),
    [tags]
  );

  // Permission helpers
  const permissions = useMemo(
    () => ({
      isAdmin: hasTag("admin"),
      isVip: hasTag("vip"),
      isWholesale: hasTag("wholesale"),
      isBeta: hasTag("beta"),
    }),
    [hasTag]
  );

  // Access control
  const canAccess = useCallback(
    (requiredTags?: string[]) => {
      if (!isAuthenticated) return false;
      if (!requiredTags || requiredTags.length === 0) return true;
      return hasAnyTag(requiredTags);
    },
    [isAuthenticated, hasAnyTag]
  );

  // Profile update mutation with comprehensive cache invalidation
  const updateProfileMutation = useMutation({
    mutationFn: async (data: UpdateCustomerInput) => {
      return orpc.shopify.updateCustomerProfile.call(data);
    },
    onSuccess: async (data: UpdateCustomerResponse) => {
      if (!data.success) {
        const errorMessage = data.error?.message || "Failed to update profile";
        toast.error("Failed to update profile", {
          description: errorMessage,
        });
        return;
      }

      toast.success("Profile updated successfully!");

      if (data.data?.customer && user) {
        const updatedUser = {
          ...user,
          ...data.data.customer,
          ...(data.data.customerAccessToken && {
            accessToken: data.data.customerAccessToken.accessToken,
          }),
        };

        // Update the session - this will trigger the JWT callback to update the token
        await update({ user: updatedUser });

        // Simplified cache invalidation - the session update handles most state management
        // Only invalidate customer-related queries that might be cached separately
        queryClient.invalidateQueries({ queryKey: ["customer"] });
      }
    },
    onError: (error: Error) => {
      toast.error("Failed to update profile", {
        description: error.message,
      });
    },
  });

  // Profile update function
  const updateProfile = useCallback(
    async (profileData: {
      firstName?: string;
      lastName?: string;
      phone?: string | null;
    }) => {
      if (!user?.accessToken) {
        throw new Error("Authentication required");
      }

      return updateProfileMutation.mutateAsync({
        customerAccessToken: user.accessToken,
        customer: profileData,
      });
    },
    [user?.accessToken, updateProfileMutation]
  );

  return useMemo(
    () => ({
      // Core user data
      user,
      session: typedSession,

      // Authentication state
      isAuthenticated,
      isLoading,
      isUnauthenticated,
      status,

      // User properties
      userId: user?.id || null,
      email: user?.email || null,
      firstName: user?.firstName || null,
      lastName: user?.lastName || null,
      fullName,
      phone: user?.phone || null,
      emailVerified: !!user?.emailVerified,
      shopifyCustomerId: user?.shopifyCustomerId || null,
      tags,

      // Token data
      accessToken: user?.accessToken || null,
      refreshToken: typedSession?.refreshToken || null,
      expiresAt: typedSession?.expiresAt || null,

      // Permissions and access control
      permissions,
      hasTag,
      hasAnyTag,
      hasAllTags,
      canAccess,

      // Profile management
      updateProfile,
      isUpdatingProfile: updateProfileMutation.isPending,
      updateProfileError: updateProfileMutation.error,

      // Session management
      updateSession: update,
    }),
    [
      user,
      typedSession,
      isAuthenticated,
      isLoading,
      isUnauthenticated,
      status,
      fullName,
      tags,
      permissions,
      hasTag,
      hasAnyTag,
      hasAllTags,
      canAccess,
      updateProfile,
      updateProfileMutation.isPending,
      updateProfileMutation.error,
      update,
    ]
  );
}

/**
 * Legacy hook for backwards compatibility
 * @deprecated Use useUser instead
 */
export const useAuth = useUser;

/**
 * Hook for permission-specific functionality
 * @deprecated Use useUser().permissions instead
 */
export function usePermissions() {
  const { permissions, tags, hasTag, hasAnyTag, hasAllTags } = useUser();
  return { ...permissions, tags, hasTag, hasAnyTag, hasAllTags };
}

/**
 * Hook for authentication status checks
 * @deprecated Use useUser() instead
 */
export function useAuthStatus() {
  const { isAuthenticated, isLoading, isUnauthenticated, status, canAccess } =
    useUser();
  const requireAuth = useCallback(() => isAuthenticated, [isAuthenticated]);

  return {
    isAuthenticated,
    isLoading,
    isUnauthenticated,
    status,
    requireAuth,
    canAccess,
  };
}
